import { Inject, Injectable } from '@angular/core';
import {
  FilterType,
  PERIODS,
  REGIONS,
  TEMPLATE_TRANSLATIONS,
  TEMPLATES,
  TEMPLATES_NA,
  TEMPLATES_AP,
  TEMPLATES_EU,
  getTemplatesAP
} from '../../filter-panel/types/filters';
import { BehaviorSubject, Observable } from 'rxjs';
import { ContractFilterOptionsModel, DistributorFilterModel, DivDataView, FilterOption, PestFilterModel, QSRFilterOptionsModel, SelectedFilterOption, FRSFilterOptionsModel } from 'src/app/global/filter-options-types';
import { DistributorFilterModelOptions } from 'src/app/global/distributor-filter-model-options';
import { ContractViewFilterOptions } from 'src/app/global/contract-view-filter-options';
import { FilterRequests } from 'src/app/core/services/filter-requests.service';
import { CountriesService } from 'src/app/core/services/countries.service';
import { QSRFilterOptions } from 'src/app/global/qsr-view-filter-options';
import { FRSFilterOptions } from 'src/app/global/frs-view-filter-options';
import { TemplateTranslationsService } from 'src/app/core/services/template-translations.service';
import { Country } from 'src/app/global/types';
import { PestFilterOptions } from 'src/app/global/pest-view-filter-options';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';


@Injectable({
  providedIn: 'root',
})
export class FilterOptionService {
  filterRequestsTest: FilterRequests | undefined;

  private selectedDivisionDataViewSubject: BehaviorSubject<DivDataView> =
    new BehaviorSubject<DivDataView>({
      division: 'Institutional',
      dataView: 'Contract',
    });

  public selectedDivisionDataView: Observable<DivDataView> =
    this.selectedDivisionDataViewSubject.asObservable();

  public setSelectedDivisionDataView(dataView: DivDataView): void {
    this.selectedDivisionDataViewSubject.next(dataView); // Set the value of selectedDivisionDataView
  }

  public getSelectedDivisionDataView(): DivDataView {
    return this.selectedDivisionDataViewSubject.getValue(); // Return the current value of the selectedDivisionDataView
  }

  private filterViewOptionsSubject: BehaviorSubject<
    FilterOption[] | undefined
  > = new BehaviorSubject<FilterOption[] | undefined>(undefined);
  public filterViewOptions: Observable<FilterOption[] | undefined> =
    this.filterViewOptionsSubject.asObservable();

  public setFilterViewOptions(filterOption: FilterOption[] | undefined): void {
    this.filterViewOptionsSubject.next(filterOption);
  }

  constructor(
    private filterRequests: FilterRequests,
    private countries: CountriesService,
    private templateTranslationsService: TemplateTranslationsService,
    @Inject(FEATURE_FLAGS) private _featureFlags: FeatureFlags,
  ) { }

  public getNames(type: FilterType): string[] {
    let names: string[] = [];

    switch (type) {
      case FilterType.REGION:
        if (this.selectedDivisionDataViewSubject.getValue().division == 'Pest' ){
              names = ['North America'];
            }
        else if (this.selectedDivisionDataViewSubject.getValue().division == 'FRS') {
          names = ['North America', 'Asia Pacific', 'Latin America'];
          if(this._featureFlags.enableFRSSIEuropeRegion){
            names.push('Europe');
          }
        }
        else if (this.selectedDivisionDataViewSubject.getValue().division == 'QSR') {
          names = ['North America'];
          if(this._featureFlags.enableAsiaPacificRegionQSR){
            names.push('Asia Pacific');
          }
          if(this._featureFlags.enableLatinAmericaSolPartQSR){
            names.push('Latin America');
          }
        }
        else {
          names = REGIONS.map((region) => region.name);
        }
        break;
      case FilterType.PERIOD:
        names = PERIODS.map((period) => period.name);
        break;
      case FilterType.TEMPLATE:
        names = TEMPLATES.map((template) => template.name);
        break;
      case FilterType.TEMPLATES_NA:
        names = TEMPLATES_NA.map((template) => template.name);
        break;
      case FilterType.TEMPLATES_AP:
        names = getTemplatesAP(this._featureFlags.enableTVDForAPInstitutional).map((template) => template.name);
        break;
      case FilterType.TEMPLATE_TRANSLATION:
        names = TEMPLATE_TRANSLATIONS.map((translation) => translation.name);
        break;
      case FilterType.TEMPLATES_EU:
        names = TEMPLATES_EU.map((template) => template.name);
        break;
    }

    return names;
  }

  public mapNameToCode(name: string, filterType: FilterType) {
    let code = '';

    switch (filterType) {
      case FilterType.REGION:
        REGIONS.forEach((option) => {
          if (name == option.name) {
            code = option.code;
            return;
          }
        });
        break;
      case FilterType.PERIOD:
        PERIODS.forEach((option) => {
          if (name == option.name) {
            code = option.code;
            return;
          }
        });
        break;
      case FilterType.TEMPLATE:
        TEMPLATES.forEach((option) => {
          if (name == option.name) {
            code = option.code;
            return;
          }
        });
        break;
      case FilterType.TEMPLATES_NA:
        TEMPLATES_NA.forEach((option) => {
          if (name == option.name) {
            code = option.code;
            return;
          }
        });
        break;
      case FilterType.TEMPLATES_EU:
        TEMPLATES_EU.forEach((option) => {
          if (name == option.name) {
            code = option.code;
            return;
          }
        });
        break;
      case FilterType.TEMPLATES_AP:
        getTemplatesAP(this._featureFlags.enableTVDForAPInstitutional).forEach((option) => {
          if (name == option.name) {
            code = option.code;
            return;
          }
        });
        break;
      case FilterType.TEMPLATE_TRANSLATION:
        TEMPLATE_TRANSLATIONS.forEach((option) => {
          if (name.toLowerCase() == option.name.toLowerCase()) {
            code = option.code;
            return;
          }
        });
        break;
    }

    return code;
  }

  public mapRegionsToCodes(names: string[]): string[] {
    return names.map((name) => this.mapNameToCode(name, FilterType.REGION));
  }

  public setFilterOptions() {
    const periodNames = this.getNames(FilterType.PERIOD);
    const regionNames = this.getNames(FilterType.REGION);

    if (this.getSelectedDivisionDataView().division == 'Distributor') {
      return DistributorFilterModelOptions(periodNames);
    }
    if (
      this.getSelectedDivisionDataView().division == 'Institutional' &&
      this.getSelectedDivisionDataView().dataView == 'Contract'
    ) {
      return ContractViewFilterOptions(periodNames, regionNames);
    }
    if (this.getSelectedDivisionDataView().division == 'QSR') {
      return QSRFilterOptions(periodNames, regionNames);
    }
    if (this.getSelectedDivisionDataView().division == 'Pest') {
      return PestFilterOptions(periodNames, regionNames);
    }
    if (this.getSelectedDivisionDataView().division == 'FRS') {
      return FRSFilterOptions(periodNames, regionNames);
    }


    return undefined;
  }

  public getPeriodNameFromCode(code: string = ''): string | undefined {
    const period = PERIODS.find((period) => period.code === code);
    return period ? period.name : undefined;
  }

  public async updateOptionsForDistributor(
    index: number,
    divDataView: string,
    filterOptions: any
  ) {
    switch (index) {
      case DistributorFilterModel.TopLevelContractGroup:
        return await this.filterRequests.getFilterOptionsByFilterType(
          divDataView,
          'TopLevelContractGroup'
        );
      case DistributorFilterModel.SecondLevelContractFilter:
        return await this.filterRequests.getFilterOptionsByFilterType(
          divDataView,
          'SecondLevelContractFilter',
          filterOptions?.[DistributorFilterModel.TopLevelContractGroup]?.selected ?? []
        );
      case DistributorFilterModel.Accts:
        return await this.filterRequests.getFilterOptionsByFilterType(
          divDataView,
          'Accts',
          filterOptions[DistributorFilterModel.TopLevelContractGroup].selected,
          filterOptions[DistributorFilterModel.SecondLevelContractFilter]
            .selected,
        );
    }
  }

  public async updateOptionsForContract(
    index: number,
    divDataView: string,
    filterOptions: any
  ) {
    switch (index) {
      case ContractFilterOptionsModel.Countries:
        return (
          await this.countries.getCountries(
            this.mapRegionsToCodes(
              filterOptions[
                ContractFilterOptionsModel.Regions
              ].selected
            ),
            divDataView
          )
        ).map((country) => country.name);
      case ContractFilterOptionsModel.TopLevelCustomerName:
        return await this.filterRequests.getTopLevelHierarchyAndCustomerNames(
          this.mapRegionsToCodes(
            filterOptions[
              ContractFilterOptionsModel.Regions
            ].selected
          ),
          this.countries.mapCountryNamesToCodes(
            filterOptions[ContractFilterOptionsModel.Countries].selected
          ),
          divDataView,
          'Institutional'
        );
      case ContractFilterOptionsModel.SecondLevelContractFilter:
        return await this.filterRequests.getH7AndCustomerNames(
          this.mapRegionsToCodes(
            filterOptions[
              ContractFilterOptionsModel.Regions
            ].selected
          ),
          this.countries.mapCountryNamesToCodes(
            filterOptions[ContractFilterOptionsModel.Countries].selected
          ),
          divDataView,
          filterOptions[ContractFilterOptionsModel.TopLevelCustomerName]
            .selected,
          'Institutional'
        );
      case ContractFilterOptionsModel.CustomerNumberName:
        return await this.filterRequests.getAccountNumbersAndNames(
          this.mapRegionsToCodes(
            filterOptions[ContractFilterOptionsModel.Regions].selected
          ),
          this.countries.mapCountryNamesToCodes(
            filterOptions[ContractFilterOptionsModel.Countries].selected
          ),
          divDataView,
          filterOptions[ContractFilterOptionsModel.TopLevelCustomerName]
            .selected,
          filterOptions[ContractFilterOptionsModel.SecondLevelContractFilter]
            .selected,
          'Institutional'
        );

    }
  }

  public async applySaveFilterOptionsForContract(
    index: number,
    divDataView: string,
    selectedOption: SelectedFilterOption
  ) {
    switch (index) {
      case ContractFilterOptionsModel.Regions:
        return this.getNames(FilterType.REGION);
      case ContractFilterOptionsModel.Countries:
        return (
          await this.countries.getCountryOptions()
        ).map((country: Country) => country.name);
      case ContractFilterOptionsModel.DateRange:
        return this.getNames(FilterType.PERIOD);
      case ContractFilterOptionsModel.TopLevelCustomerName:
        return await this.filterRequests.getTopLevelHierarchyAndCustomerNames(
          this.mapRegionsToCodes(
            selectedOption.Regions ?? [],
          ),
          this.countries.mapCountryNamesToCodes(
            selectedOption.Countries ?? []
          ),
          divDataView,
          'Institutional'
        );
      case ContractFilterOptionsModel.SecondLevelContractFilter:
        return await this.filterRequests.getH7AndCustomerNames(
          this.mapRegionsToCodes(
            selectedOption.Regions ?? []
          ),
          this.countries.mapCountryNamesToCodes(
            selectedOption.Countries ?? []
          ),
          divDataView,
          selectedOption.H5 ?? [],
          'Institutional'
        );
      case ContractFilterOptionsModel.CustomerNumberName:
        return await this.filterRequests.getAccountNumbersAndNames(
          this.mapRegionsToCodes(
            selectedOption.Regions ?? []
          ),
          this.countries.mapCountryNamesToCodes(
            selectedOption.Countries ?? []
          ),
          divDataView,
          selectedOption.H5 ?? [],
          selectedOption.H7 ?? [],
          'Institutional'
        );
      default:
        return [];
    }
  }

  public async applySaveFilterOptionsForQSR(
    index: number,
    divDataView: string,
    selectedOption: SelectedFilterOption
  ) {
    switch (index) {
      case QSRFilterOptionsModel.Regions:
        return this.getNames(FilterType.REGION);
      case QSRFilterOptionsModel.Countries:
        return (
          await this.countries.getCountryOptions()
        ).map((country) => country.name);
      case QSRFilterOptionsModel.TopLevelCustomerName:
        return await this.filterRequests.getTopLevelHierarchyAndCustomerNames(
          this.mapRegionsToCodes(
            selectedOption.Regions ?? []
          ),
          this.countries.mapCountryNamesToCodes(
            selectedOption.Countries ?? []
          ),
          divDataView,
          'QSR'
        );
      case QSRFilterOptionsModel.SecondLevelContractFilter:
        return await this.filterRequests.getH7AndCustomerNames(
          this.mapRegionsToCodes(
            selectedOption.Regions ?? []
          ),
          this.countries.mapCountryNamesToCodes(
            selectedOption.Countries ?? []
          ),
          divDataView,
          selectedOption.H5 ?? [],
          'QSR'
        );
      case QSRFilterOptionsModel.CustomerNumberName:
        return await this.filterRequests.getAccountNumbersAndNames(
          this.mapRegionsToCodes(
            selectedOption.Regions ?? []
          ),
          this.countries.mapCountryNamesToCodes(
            selectedOption.Countries ?? []
          ),
          divDataView,
          selectedOption.H5 ?? [],
          selectedOption.H7 ?? [],
          'QSR'
        );
      case QSRFilterOptionsModel.DateRange:
        return this.getNames(FilterType.PERIOD);
    }
  }

  public async applySaveFilterOptionsForFRS(
    index: number,
    divDataView: string,
    selectedOption: SelectedFilterOption
  ) {
    switch (index) {
      case FRSFilterOptionsModel.Regions:
        return this.getNames(FilterType.REGION);
      case FRSFilterOptionsModel.Countries:
        return (
          await this.countries.getCountryOptions()
        ).map((country) => country.name);
      case FRSFilterOptionsModel.TopLevelCustomerName:
        return await this.filterRequests.getTopLevelHierarchyAndCustomerNames(
          this.mapRegionsToCodes(
            selectedOption.Regions ?? [],
          ),
          this.countries.mapCountryNamesToCodes(
            selectedOption.Countries ?? []
          ),
          divDataView,
          'FRS'
        );
      case FRSFilterOptionsModel.SecondLevelContractFilter:
        return await this.filterRequests.getH7AndCustomerNames(
          this.mapRegionsToCodes(
            selectedOption.Regions ?? []
          ),
          this.countries.mapCountryNamesToCodes(
            selectedOption.Countries ?? []
          ),
          divDataView,
          selectedOption.H5 ?? [],
          'FRS'
        );
      case FRSFilterOptionsModel.CustomerNumberName:
        return await this.filterRequests.getAccountNumbersAndNames(
          this.mapRegionsToCodes(
            selectedOption.Regions ?? [],
          ),
          this.countries.mapCountryNamesToCodes(
            selectedOption.Countries ?? []
          ),
          divDataView,
          selectedOption.H5 ?? [],
          selectedOption.H7 ?? [],
          'FRS'
        );
      case FRSFilterOptionsModel.DateRange:
        return this.getNames(FilterType.PERIOD);
    }
  }

  public async applySaveFilterOptionsForDistributor(
    index: number,
    divDataView: string,
    selectedOption: SelectedFilterOption
  ) {
    switch (index) {
      case DistributorFilterModel.TopLevelContractGroup:
        return await this.filterRequests.getFilterOptionsByFilterType(
          divDataView,
          'TopLevelContractGroup'
        );
      case DistributorFilterModel.SecondLevelContractFilter:
        return await this.filterRequests.getFilterOptionsByFilterType(
          divDataView,
          'SecondLevelContractFilter',
          selectedOption.TopLevelContractGroup ?? []
        );
      case DistributorFilterModel.Accts:
        return await this.filterRequests.getFilterOptionsByFilterType(
          divDataView,
          'Accts',
          selectedOption.TopLevelContractGroup ?? [],
          selectedOption.SecondLevelGroup ?? [],
        );
      case DistributorFilterModel.Period:
        return this.getNames(FilterType.PERIOD);
    }
  }


  public async updateOptionsForFilter(
    index: number,
    divDataView: string,
    filterOptions: any,
    filterModel: any, // Pass the specific filter model here (QSR or Pest)
  ) {
    switch (index) {
      case filterModel.Countries:
        return (
          await this.countries.getCountries(
            this.mapRegionsToCodes(
              filterOptions[filterModel.Regions].selected
            ),
            divDataView
          )
        ).map((country) => country.name);

      case filterModel.TopLevelCustomerName:
        return await this.filterRequests.getTopLevelHierarchyAndCustomerNames(
          this.mapRegionsToCodes(
            filterOptions[filterModel.Regions].selected
          ),
          this.countries.mapCountryNamesToCodes(
            filterOptions[filterModel.Countries].selected
          ),
          divDataView,
          divDataView
        );

      case filterModel.SecondLevelContractFilter:
        return await this.filterRequests.getH7AndCustomerNames(
          this.mapRegionsToCodes(
            filterOptions[filterModel.Regions].selected,
          ),
          this.countries.mapCountryNamesToCodes(
            filterOptions[filterModel.Countries].selected
          ),
          divDataView,
          filterOptions[filterModel.TopLevelCustomerName].selected,
          divDataView
        );

      case filterModel.CustomerNumberName:
        return await this.filterRequests.getAccountNumbersAndNames(
          this.mapRegionsToCodes(
            filterOptions[filterModel.Regions].selected
          ),
          this.countries.mapCountryNamesToCodes(
            filterOptions[filterModel.Countries].selected
          ),
          divDataView,
          filterOptions[filterModel.TopLevelCustomerName].selected,
          filterOptions[filterModel.SecondLevelContractFilter].selected,
          divDataView
        );
    }
  }
}
